import { camelCase } from 'lodash';
import React, { useState } from 'react';

import type { AssetMetaData, URIHint } from '@swf/core/types/Notification';

import iceLockedImage from '../../assets/icons/ice-locked-icon.svg';
import defaultChestSprite from '../../assets/images/default-chest-sprite.png';
import defaultMultiChestImage from '../../assets/images/default-multi-chest-image.png';
import timeLockedImage from '../../assets/images/time-locked-icon.svg';
import defaultChestLogo from '../../assets/logos/mpc-plaque-default.png';
import {
  ChestAssetType,
  LegacyChestLogoMap,
  LegacyChestSpriteMap,
  NotificationType
} from '../../enums';
import { getChestAssetURL } from '../../utils';
import ChestSpriteView from '../ChestSprite/ChestSprite';

import styles from './ChestImageWrapper.scss';

export enum ChestImageVariant {
  Default = 'ChestImageDefault'
}

export interface ChestImageData {
  chestSprite?: string;
  chestLogo?: string;
}

export interface Props {
  type: NotificationType;
  metaData: AssetMetaData[];
  variant: ChestImageVariant;
  cdnBase: string;
  customSkin?: string;
  logoName?: string;
}

export interface GraphicalAssets {
  [ChestAssetType.Logo]?: string;
  [ChestAssetType.ChestSkin]?: string;
  [ChestAssetType.NotificationHeader]?: string;
}

/**
 * @param metaData
 * Convert the given meta data to uris for graphical assets, keyed by type.
 * eg.
 *  {
 *    skin: 'http://cdn.example.com/assets/skins/274/chest_summer.png',
 *    logo: 'http://cdn.exmaple.com/assets/logos/123/logo1.png
 *  }
 */
const metaDataToGraphicalAssets = (
  metaData: AssetMetaData[],
  cdnBase: string,
  customSkin?: string,
  logoName?: string
): GraphicalAssets => {
  const graphicalAssets: GraphicalAssets = {};
  if (!metaData || metaData.length === 0 || !cdnBase) {
    // Legacy chest / logo handling - baked in assets - define mapping
    customSkin = customSkin || 'default';
    logoName = logoName || 'none';
    graphicalAssets[ChestAssetType.ChestSkin] =
      LegacyChestSpriteMap[customSkin.toLowerCase() as keyof typeof LegacyChestSpriteMap];
    graphicalAssets[ChestAssetType.Logo] =
      LegacyChestLogoMap[logoName.toLowerCase() as keyof typeof LegacyChestLogoMap];
  } else {
    metaData.forEach((data) => {
      const { uriHint } = data;
      const { path } = uriHint as URIHint;
      graphicalAssets[data.type as keyof GraphicalAssets] =
        getChestAssetURL(cdnBase, path) ?? undefined;
    });
  }
  return graphicalAssets;
};

/**
 * Image load failure will trigger default image load
 */
const missingImageCheck = ({
  chestSkin,
  setChestSpriteValid
}: {
  chestSkin: string;
  setChestSpriteValid: (param: boolean) => void;
}) => (
  <img
    style={{ display: 'none' }}
    alt=""
    src={chestSkin}
    onError={() => setChestSpriteValid(false)}
  />
);

/**
 * Component wrapper - to get graphical assets, lock icon and render appropriate chest type
 * multiple, ice locked, time locked or available (default)
 * @param props
 */

const ChestImageWrapper: React.FC<Props> = ({
  type,
  metaData,
  variant = ChestImageVariant.Default,
  cdnBase,
  logoName,
  customSkin
}) => {
  const [isChestSpriteValid, setChestSpriteValid] = useState(true);
  const metaDataToAssetMap = metaDataToGraphicalAssets(metaData, cdnBase, customSkin, logoName);
  let { [ChestAssetType.ChestSkin]: chestSkin = defaultChestSprite as string } = metaDataToAssetMap;
  const { [ChestAssetType.Logo]: chestLogo = defaultChestLogo } = metaDataToAssetMap;
  const useMultiChestImage = type === NotificationType.ChestRewardMultiple;
  if (!isChestSpriteValid) {
    chestSkin = defaultChestSprite as string;
  }
  // Set defaults for available
  const defaultChestDisplayImage = defaultMultiChestImage;
  let lockedIcon;
  let multiStyle = '';

  if (type === NotificationType.ChestRewardMultiple) {
    multiStyle = styles.multiChestImage;
  } else if (type === NotificationType.ChestRewardIceLocked) {
    lockedIcon = iceLockedImage;
  } else if (type === NotificationType.ChestRewardTimeLocked) {
    lockedIcon = timeLockedImage;
  }

  return (
    <div
      className={`${styles.chestImageWrapper} ${styles[camelCase(variant) as keyof typeof styles]}`}
    >
      {/* Add hidden images to check for presence of CDN images */}
      {missingImageCheck({ chestSkin, setChestSpriteValid })}
      {useMultiChestImage && (
        <div
          className={`${styles.defaultChestImage} ${multiStyle}`}
          style={{ backgroundImage: `url(${defaultChestDisplayImage})` }}
        />
      )}
      {!useMultiChestImage && (
        <ChestSpriteView skin={chestSkin} logo={chestLogo} icon={lockedIcon} />
      )}
    </div>
  );
};

export default ChestImageWrapper;
